import {
	Dispatch,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	SetStateAction,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./ListView.module.less";
import {
	EMPTY_ARRAY,
	delay,
	ensureNotNil,
	isNotNil,
	setTrackingId,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { Header } from "./Header/Header";
import { NoEmailsResult } from "./NoEmailsResult/NoEmailsResult";
import {
	EmailActions,
	EmailFilterSelector,
	EmailListItemModel,
	EmailSortDirection,
	EmailSortOrder,
	ExchangeEmailScreenSelectionModel,
	FolderCounters,
	IFolderViewModel,
	LazyLoadedChildren,
	listFromExchange,
	ListFromExchangePossibleWithFolderCountersResponse,
	listFromExchangeWithFolderCounters,
	MessageFlagState,
	MessageReadState
} from "client/Api/Email/email";
import { newGuid } from "client_legacy/ndaw/guid";
import { ExchangeEmailId } from "client/Api/Email/common";
import {
	BusyIndicator,
	computeContextMenuTitle,
	ContextMenu,
	FormattedText,
	ScrollableVirtualList,
	useContextMenu,
	SelectAllCheckboxValue,
	ScrollableVirtualListHandle
} from "client/Components";
import { DragTooltip } from "client/Components/DragTooltip/DragTooltip";
import { localize } from "client_legacy/ndaw/culture";
import { Item, ItemHandle } from "./Item/Item";
import {
	ItemSelectionKind,
	SelectableItemOptions,
	useIsTouchDevice,
	useSelection,
	useStableImperativeCallback,
	useSynchronizedRef,
	useValueEqualMemo
} from "client/Hooks";
import { SelectMode } from "client/Misc/commonTypes";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import { EmailDragStartCallbackData, EmailDraggableData } from "../dragAndDrop";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	EmailResponseKind,
	EmailSessionState,
	EmailSessionStateKind
} from "../EmailPage/EmailPage";
import { never } from "client_legacy/ndaw/errors";
import { EmailOrdering } from "./emailOrdering";
import { useKeyboardNavigation } from "./useKeyboardNavigation";
import {
	EmailActionModel,
	EmailSelectionAction,
	EmailSelectionActionContext,
	getSelectionActions
} from "../Actions/selectionActions";
import { useUploadAttachmentInProgressDialog } from "../Dialogs/UploadAttachmentInProgressDialog";
import { TimeZone } from "client/DateAndTime/timezones";

export type ListViewHandle = {
	processUpdate: (emailChanges: readonly EmailListItemModel[]) => void;
	removeItems: (ids: readonly ExchangeEmailId[]) => void;
	getNextEmail: (
		id: ExchangeEmailId,
		idsToExclude: readonly ExchangeEmailId[]
	) => EmailListItemModel | undefined;
	changeReadStateOfItems: (ids: readonly ExchangeEmailId[], newState: MessageReadState) => void;
	changeFlagStateOfItems: (ids: readonly ExchangeEmailId[], newState: MessageFlagState) => void;
	makeAwareAboutLastActionChange: (id: ExchangeEmailId, action: EmailActions) => void;
	refresh: () => void;
	changeSelectAll: (selectAll: boolean) => void;
	clearSelection(): void;
};

export type ListViewProps = {
	folder: IFolderViewModel<LazyLoadedChildren>;
	smtpAddress: string;
	emailSession: EmailSessionState;
	isMobile: boolean;
	counters?: FolderCounters;
	selectMode: SelectMode;
	selectedItems: readonly EmailActionModel[];
	isAttachmentUploadInProgress: boolean;
	onComposeNewEmail(): void;
	onWatermarkChange: (watermark: string) => void;
	onEmailsChange: (hasAnyEmail: boolean) => void;
	onOpenEmail: (emailId: string, isForEditing: boolean) => void;
	onFolderCountersRetrieved: (counters: FolderCounters) => void;
	onSelectModeChange(selectMode: SelectMode): void;
	onSelectionChange(selectedItems: readonly EmailActionModel[]): void;
	onMoveEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onDeleteEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onFlagStatusChange(
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void;
	onReadStatusChange(
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void;
	onRespondToEmail(
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	): void;
	onFolderReadStatusChange(
		smtpAddress: string,
		folderId: string,
		status: MessageReadState
	): Promise<void>;
	timezone: TimeZone;
};

export const ListView = forwardRef(
	(props: ListViewProps, ref: Ref<ListViewHandle>): ReactElement => {
		const {
			folder,
			smtpAddress,
			emailSession,
			isMobile,
			counters,
			selectMode,
			selectedItems,
			isAttachmentUploadInProgress,
			onComposeNewEmail,
			onWatermarkChange,
			onEmailsChange,
			onOpenEmail,
			onFolderCountersRetrieved,
			onSelectModeChange,
			onSelectionChange,
			onMoveEmails,
			onDeleteEmails,
			onFlagStatusChange,
			onReadStatusChange,
			onRespondToEmail,
			onFolderReadStatusChange,
			timezone
		} = props;

		const [isBusy, setIsBusy] = useState<boolean>(false);
		const [isListBusy, setIsListBusy] = useState<boolean>(false);
		const [sortOrder, setSortOrder] = useState<Exclude<EmailSortOrder, EmailSortOrder.None>>(
			EmailSortOrder.Date
		);
		const [sortDirection, setSortDirection] = useState<EmailSortDirection>(
			EmailSortDirection.Descending
		);
		const [filter, setFilter] = useState<EmailFilterSelector | null>(null);
		const [numberOfDraggedEmails, setNumberOfDraggedEmails] = useState<number>(0);
		// With keyboard navigation, we only want to open the email once the user has stopped navigation
		// for an amount of time, to reduce the potential spamm of loading email details. However due to this
		// we need to have this intermediate state as we cannot rely on the opened email from the "emailSession".
		const [pendingOrOpenedEmailId, setPendingOrOpenedEmailId] = useState<string | null>(null);

		const [itemsState, setItemsState] = useState<ItemsState>({
			items: [],
			hasMore: true
		});

		const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
			value: "",
			version: newGuid()
		});

		const containerRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
		const virtualListRef: RefObject<ScrollableVirtualListHandle<EmailListItemWithMailbox> | null> = useRef<ScrollableVirtualListHandle<EmailListItemWithMailbox>>(null);
		const dragTooltipRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const itemRefs: ReadonlyRefObject<Map<string, ItemHandle>> = useRef<Map<string, ItemHandle>>(new Map());
		const pendingOpenEmailCancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);
		const selectedItemsRef: ReadonlyRefObject<readonly EmailActionModel[]> = useSynchronizedRef(selectedItems);
		const [uploadAttachmentInProgressDialog, openUploadAttachmentInProgressDialog] = useUploadAttachmentInProgressDialog();

		const isTouchDevice: boolean = useIsTouchDevice();

		const [
			onContextMenu,
			contextMenuProps
		] = useContextMenu(containerRef);

		const openedEmailId = useMemo(
			(): ExchangeEmailId | null => {
				switch (emailSession.kind) {
					case EmailSessionStateKind.None:
					case EmailSessionStateKind.NewEmail:
						return null;
					case EmailSessionStateKind.EditDraft:
						if (!emailSession.response) {
							return emailSession.emailId;
						}

						return emailSession.response
							.originalEmailId;
					case EmailSessionStateKind.ViewEmail:
						return emailSession.emailId;
					default:
						never(emailSession);
				}
			},
			[emailSession]
		);

		const deleteSelectedEmails = useCallback(
			(): void => {
				if (selectedItems.length === 0) {
					return;
				}
				onDeleteEmails(
					selectedItems.map(x => ({
						emailId: x.emailId,
						isRead: x.isRead
					})),
					smtpAddress
				);
			},
			[
				onDeleteEmails,
				selectedItems,
				smtpAddress
			]
		);

		const checkIfNoUploadsInProgressOrShowError = useCallback(
			(): boolean => {
				if (!isAttachmentUploadInProgress) {
					return true;
				}

				openUploadAttachmentInProgressDialog();

				return false;
			},
			[
				isAttachmentUploadInProgress,
				openUploadAttachmentInProgressDialog
			]
		);

		const selectionActions = useMemo(
			(): readonly EmailSelectionAction[] => {
				return getSelectionActions(
					{
						move: onMoveEmails,
						delete: onDeleteEmails,
						changeFlagStatus: onFlagStatusChange,
						changeReadStatus: onReadStatusChange,
						respondToEmail: onRespondToEmail
					},
					checkIfNoUploadsInProgressOrShowError,
					checkIfNoUploadsInProgressOrShowError
				);
			},
			[
				onDeleteEmails,
				onFlagStatusChange,
				onMoveEmails,
				onReadStatusChange,
				onRespondToEmail,
				checkIfNoUploadsInProgressOrShowError
			]
		);

		const selectableEmails = useMemo(
			(): readonly EmailActionModel[] => itemsState
				.items
				.map(convertToEmailActionModel),
			[itemsState.items]
		);

		const {
			selectionFor,
			multiSelection
		} = useSelection(
			selectableEmails,
			"emailId",
			{
				isMultiSelect: true,
				selectedItems,
				setSelectedItems: onSelectionChange
			}
		);

		const requestParams = useMemo(
			(): ExchangeEmailScreenSelectionModel => ({
				folder: {
					smtpAddress: smtpAddress
						? {
							isSet: true,
							value: smtpAddress
						}
						: { isSet: false },
					folderId: folder.folderId.id
				},
				filterSelector: filter,
				searchQuery: searchTerm.value,
				sortOrder,
				sortDirection
			}),
			[
				filter,
				folder.folderId
					.id,
				searchTerm.value,
				sortDirection,
				sortOrder,
				smtpAddress
			]
		);

		const itemLoadingAbortController: RefObject<AbortController | undefined> = useRef<AbortController>(undefined);
		const stableSelectionChangeHandler: (selectedItems: readonly EmailActionModel[]) => void = useStableImperativeCallback(onSelectionChange);
		const stableSelectModeChangeHandler: (selectMode: SelectMode) => void = useStableImperativeCallback(onSelectModeChange);

		useEffect(
			(): (() => void) => {
				stableSelectionChangeHandler([]);

				return (): void => {
					// We intentionally want to cancel the last pending request
					// eslint-disable-next-line react-hooks/exhaustive-deps
					itemLoadingAbortController.current
						?.abort();

					stableSelectionChangeHandler([]);
					stableSelectModeChangeHandler(SelectMode.Disabled);
				};
			},
			[
				stableSelectModeChangeHandler,
				stableSelectionChangeHandler
			]
		);

		const loadNextItems: LoadNextItems = useLoadNextItems(
			itemLoadingAbortController,
			openedEmailId,
			setIsBusy,
			setItemsState,
			onWatermarkChange,
			onFolderCountersRetrieved,
			virtualListRef
		);

		const reloadHandler = useCallback(
			(requestParams: ExchangeEmailScreenSelectionModel): void => {
				const newItemState: ItemsState = {
					items: [],
					hasMore: true
				};

				stableSelectionChangeHandler(EMPTY_ARRAY);
				setItemsState(newItemState);
				loadNextItems(newItemState, requestParams);
			},
			[loadNextItems, stableSelectionChangeHandler]
		);

		useEffect(
			(): void => {
				reloadHandler(requestParams);
			},
			[reloadHandler, requestParams]
		);

		useEffect(
			(): void => {
				onEmailsChange(
					itemsState.items
						.length !== 0
				);
			},
			[itemsState.items, onEmailsChange]
		);

		const mutateItems = useCallback(
			(
				ids: readonly ExchangeEmailId[],
				mutationHandler: (item: EmailListItemWithMailbox) => EmailListItemWithMailbox
			): void => {
				setItemsState(
					(x): ItemsState => {
						const idsToUpdate = new Set<string>(ids.map(x => x.id));

						const items: readonly EmailListItemWithMailbox[] = x.items
							.map((x): EmailListItemWithMailbox => {
								if (!idsToUpdate.has(x.emailId.id)) {
									return x;
								}

								return mutationHandler(x);
							});

						return {
							...x,
							items
						};
					}
				);
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): ListViewHandle => ({
				processUpdate: (emailChanges: readonly EmailListItemModel[]): void => {
					processUpdate(
						emailChanges,
						setItemsState,
						filter,
						requestParams
					);
				},
				getNextEmail: (
					id: ExchangeEmailId,
					idsToExclude: readonly ExchangeEmailId[]
				): EmailListItemModel | undefined => {
					const items: readonly EmailListItemWithMailbox[] = itemsState.items;

					const index: number = items.findIndex(x => x.emailId.id === id.id);
					if (index === -1) {
						return undefined;
					}

					const ignoredIds = new Set<string>(idsToExclude.map(x => x.id));

					return items.slice(index)
						.find(x => !ignoredIds.has(x.emailId.id));
				},
				removeItems: (ids: readonly ExchangeEmailId[]): void => {
					setItemsState(
						(x): ItemsState => {
							const removedIds = new Set<string>(ids.map(x => x.id));

							const items: readonly EmailListItemWithMailbox[] = x.items
								.filter(x => !removedIds.has(x.emailId.id));

							return {
								...x,
								items
							};
						}
					);
				},
				changeReadStateOfItems: (ids: readonly ExchangeEmailId[], newState: MessageReadState): void => {
					mutateItems(
						ids,
						(x): EmailListItemWithMailbox => ({
							...x,
							isRead: newState === MessageReadState.Read
						})
					);
				},
				changeFlagStateOfItems: (ids: readonly ExchangeEmailId[], newState: MessageFlagState): void => {
					mutateItems(
						ids,
						(x): EmailListItemWithMailbox => ({
							...x,
							isFlagged: newState === MessageFlagState.Flagged
						})
					);
				},
				makeAwareAboutLastActionChange: (id: ExchangeEmailId, action: EmailActions): void => {
					mutateItems(
						[id],
						(x): EmailListItemWithMailbox => ({
							...x,
							lastAction: action
						})
					);
				},
				refresh: (): void => {
					reloadHandler(requestParams);
				},
				changeSelectAll: (selectAll: boolean) => {
					if (selectAll) {
						multiSelection.selectAll();
					} else {
						multiSelection.selectNone();
					}
				},
				clearSelection: (): void => {
					stableSelectionChangeHandler(EMPTY_ARRAY);
				}
			}),
			[
				filter,
				itemsState.items,
				multiSelection,
				mutateItems,
				reloadHandler,
				requestParams,
				stableSelectionChangeHandler
			]
		);

		const enableSelectModeHandler = useCallback(
			(): void => {
				onSelectModeChange(SelectMode.FromLongTouch);
			},
			[onSelectModeChange]
		);

		useEffect(
			(): void => {
				if (selectMode === SelectMode.FromLongTouch
					&& selectedItems.length === 0
				) {
					onSelectModeChange(SelectMode.Disabled);
				}
			},
			[
				selectMode,
				selectedItems.length,
				onSelectModeChange
			]
		);

		useEffect(
			(): void => {
				if (selectMode === SelectMode.Disabled
					&& isTouchDevice
				) {
					onSelectionChange(EMPTY_ARRAY);
				}
			},
			[
				selectMode,
				isTouchDevice,
				onSelectionChange
			]
		);

		const canOpenEmail = useCallback(
			(): boolean => {
				return checkIfNoUploadsInProgressOrShowError();
			},
			[checkIfNoUploadsInProgressOrShowError]
		);

		const openEmailHandler = useCallback(
			(emailId: ExchangeEmailId, isDraft: boolean): void => {
				if (!canOpenEmail()) {
					return;
				}

				pendingOpenEmailCancellationRef.current
					?.abort();

				setPendingOrOpenedEmailId(emailId.id);
				onOpenEmail(emailId.id, isDraft);
			},
			[canOpenEmail, onOpenEmail]
		);

		const debouncedOpenEmailHandler = useCallback(
			async (email: EmailListItemModel): Promise<void> => {
				if (!canOpenEmail()) {
					return;
				}

				const {
					emailId
				} = email;

				setPendingOrOpenedEmailId(emailId.id);

				pendingOpenEmailCancellationRef.current
					?.abort();

				try {
					const openEmailCancellation = new AbortController();
					pendingOpenEmailCancellationRef.current = openEmailCancellation;

					await delay(400, openEmailCancellation.signal);
					openEmailHandler(email.emailId, email.isDraft);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
				}
			},
			[canOpenEmail, openEmailHandler]
		);

		const itemDragStartHandler = useCallback(
			(initiatorEmailInfo: EmailIdWithIsReadStatus): EmailDragStartCallbackData => {

				let data: EmailDraggableData;
				if (selectedItems.some(e => e.emailId.id === initiatorEmailInfo.emailId.id)) {
					data = {
						emails: selectedItems.map(x => ({
							emailId: x.emailId,
							isRead: x.isRead
						})),
						sourceFolderId: folder.folderId,
						smtpAddress,
						draggableIdentity: "emailItem"
					};
				} else {
					data = {
						emails: [initiatorEmailInfo],
						sourceFolderId: folder.folderId,
						smtpAddress,
						draggableIdentity: "emailItem"
					};
				}

				setNumberOfDraggedEmails(data.emails.length);

				return {
					draggableData: data,
					tooltip: ensureNotNil(dragTooltipRef.current)
				};
			},
			[
				folder.folderId,
				selectedItems,
				smtpAddress
			]
		);

		const deleteEmailHandler = useCallback(
			(email: EmailListItemModel): void => {
				if (email.emailId.id === pendingOrOpenedEmailId) {
					pendingOpenEmailCancellationRef.current
						?.abort();

					setPendingOrOpenedEmailId(openedEmailId?.id ?? null);
				}

				const {
					emailId,
					isRead
				} = email;

				onDeleteEmails(
					[{ emailId, isRead }],
					smtpAddress
				);
			},
			[
				onDeleteEmails,
				openedEmailId?.id,
				pendingOrOpenedEmailId,
				smtpAddress
			]
		);

		const {
			navigateToResultsHandler,
			keyboardNextItemHandler,
			keyboardPreviousItemHandler,
			keyboardNextPageItemHandler,
			keyboardPreviousPageItemHandler,
			keyboardFirstItemHandler,
			keyboardLastItemHandler,
			keyboardDeleteHandler
		} = useKeyboardNavigation(
			itemsState.items,
			selectMode,
			itemRefs,
			virtualListRef,
			selectionFor,
			onSelectionChange,
			deleteSelectedEmails,
			deleteEmailHandler,
			debouncedOpenEmailHandler
		);

		const loadMoreItemsHandler = useCallback(
			(): void => {
				loadNextItems(itemsState, requestParams);
			},
			[
				itemsState,
				loadNextItems,
				requestParams
			]
		);

		const setItemRef = useCallback(
			(id: string, ref: ItemHandle | null): void => {
				if (!ref) {
					itemRefs.current
						.delete(id);
				} else {
					itemRefs.current
						.set(id, ref);
				}
			},
			[]
		);

		const selectOpenedEmailHandler = useCallback(
			(): void => {
				const openedEmail: EmailListItemWithMailbox | undefined = itemsState
					.items
					.find(
						x => x.emailId.id === openedEmailId?.id
					);

				if (!openedEmail) {
					return;
				}

				const selectable: SelectableItemOptions = selectionFor(openedEmail);

				if (selectable && !selectable.isSelected) {
					selectable.onSelect(ItemSelectionKind.Append);
				}
			},
			[
				itemsState.items,
				openedEmailId?.id,
				selectionFor
			]
		);

		const singleSelectedEmail: EmailIdWithIsDraftStatus | undefined = useSingleSelectedItem(selectedItems);
		const stableOpenEmailHandler: typeof openEmailHandler = useStableImperativeCallback(openEmailHandler);

		useEffect(
			(): void => {
				if (isMobile) {
					return;
				}

				if (isNotNil(singleSelectedEmail)) {
					stableOpenEmailHandler(singleSelectedEmail.emailId, singleSelectedEmail.isDraft);
				}
			},
			[
				isMobile,
				stableOpenEmailHandler,
				singleSelectedEmail
			]
		);

		useEffect(
			(): void => {
				pendingOpenEmailCancellationRef.current
					?.abort();

				if (openedEmailId) {
					setPendingOrOpenedEmailId(openedEmailId.id);

					const items: readonly EmailActionModel[] = selectedItemsRef.current;

					if (items.length > 1
						|| (items.length === 1 && items[0].emailId.id !== openedEmailId.id)
					) {
						onSelectionChange(EMPTY_ARRAY);
					}
				} else {
					setPendingOrOpenedEmailId(null);
				}
			},
			[
				openedEmailId,
				selectedItemsRef,
				onSelectionChange
			]
		);

		const contextMenuActionContext = useMemo(
			(): EmailSelectionActionContext => {
				if (selectedItems.length === 0
					&& pendingOrOpenedEmailId !== null
				) {
					const pendingOrOpenedItem: EmailListItemWithMailbox | undefined = itemsState.items
						.find(x => x.emailId.id === pendingOrOpenedEmailId);

					if (pendingOrOpenedItem) {
						return {
							effectiveSelection: [pendingOrOpenedItem],
							selectedItems: selectedItems
						};
					}
				}

				return {
					selectedItems: selectedItems,
					effectiveSelection: selectedItems
				};
			},
			[
				itemsState.items,
				pendingOrOpenedEmailId,
				selectedItems
			]
		);

		useEffect(
			(): (() => void) => {
				return (): void => {
					pendingOpenEmailCancellationRef.current
						?.abort();
				};
			},
			[]
		);

		const refreshHandler = useCallback(
			(): void => {
				reloadHandler(requestParams);
			},
			[reloadHandler, requestParams]
		);

		const folderReadStatusChangeHandler = useCallback(
			async (
				smtpAddress: string,
				folderId: string,
				status: MessageReadState
			): Promise<void> => {
				try {
					setIsListBusy(true);
					await onFolderReadStatusChange(
						smtpAddress,
						folderId,
						status
					);
				} finally {
					setIsListBusy(false);
				}
			},
			[onFolderReadStatusChange]
		);

		const globalSelectionChangeHandler = useCallback(
			(shouldSelectAll: boolean): void => {
				if (shouldSelectAll) {
					multiSelection.selectAll();
				} else {
					multiSelection.selectNone();
				}
			},
			[multiSelection]
		);

		const globalSelectionStatus: SelectAllCheckboxValue = multiSelection.areAllSelected
			? true
			: multiSelection.selectionCount > 0
				? "mixed"
				: false;

		return (
			<div {...setTrackingId("email-list-view")} className={styles.root}>
				<div className={styles.header}>
					<Header
						smtpAddress={smtpAddress}
						folderId={folder.folderId.id}
						folderName={folder.name}
						searchTerm={searchTerm}
						onSearch={setSearchTerm}
						sortOrder={sortOrder}
						sortDirection={sortDirection}
						onSortOrderChange={setSortOrder}
						onSortDirectionChange={setSortDirection}
						filter={filter}
						onFilterChange={setFilter}
						onNavigateToResults={navigateToResultsHandler}
						globalSelectionStatus={globalSelectionStatus}
						onGlobalSelectionChange={globalSelectionChangeHandler}
						counters={counters}
						onComposeNewEmail={onComposeNewEmail}
						onRefreshList={refreshHandler}
						onFolderReadStatusChange={folderReadStatusChangeHandler}
						isMobile={isMobile}
					/>
				</div>
				<div
					className={styles.itemsContainer}
					ref={containerRef}
				>
					{!isBusy && itemsState.items.length === 0
						? <NoEmailsResult searchTerm={searchTerm.value} />
						: (
							<>
								<ScrollableVirtualList
									items={itemsState.items}
									estimatedHeightOfAnItem={96}
									itemKey={getUniqueKey}
									ref={virtualListRef}
									trackingId="email-list-items"
									isSmoothScrollingDisabled
									renderAfterItems={
										<>
											<ScrollableVirtualList.LoadMoreIndicator
												isLoadingData={isBusy}
												hasMoreItems={itemsState.hasMore}
												onRequestMoreItems={loadMoreItemsHandler}
											/>
											<ScrollableVirtualList.MobilePlusMenuSpacing />
										</>
									}
								>
									{(item: EmailListItemWithMailbox, index: number): ReactElement => {
										const isOpened: boolean = isNotNil(pendingOrOpenedEmailId)
											&& pendingOrOpenedEmailId === item.emailId.id;

										return (
											<Item
												model={item}
												isOpened={isOpened}
												selectable={selectionFor(item)}
												selectionActions={selectionActions}
												isTouch={isTouchDevice}
												isInSelectMode={selectMode !== SelectMode.Disabled}
												isContextMenuDisabled={isTouchDevice || isMobile}
												onEnableSelectMode={enableSelectModeHandler}
												canOpenEmail={canOpenEmail}
												hasSelectedItems={multiSelection.selectionCount > 0}
												isInSentFolder={folder.isSent}
												onDragStart={itemDragStartHandler}
												onKeyboardNextItem={(isShift) => keyboardNextItemHandler(index, isShift)}
												onKeyboardPreviousItem={(isShift) => keyboardPreviousItemHandler(index, isShift)}
												onKeyboardNextPageItem={(isShift) => keyboardNextPageItemHandler(index, isShift)}
												onKeyboardPreviousPageItem={(isShift) => keyboardPreviousPageItemHandler(index, isShift)}
												onKeyboardFirstItem={(isShift) => keyboardFirstItemHandler(index, isShift)}
												onKeyboardLastItem={(isShift) => keyboardLastItemHandler(index, isShift)}
												onDelete={() => keyboardDeleteHandler(index)}
												ref={(x) => setItemRef(item.emailId.id, x)}
												onSelectOpenedEmail={selectOpenedEmailHandler}
												onOpenEmail={() => openEmailHandler(item.emailId, item.isDraft)}
												onContextMenu={onContextMenu}
												timezone={timezone}
											/>
										);
									}}
								</ScrollableVirtualList>

								{contextMenuProps && (
									<ContextMenu
										{...contextMenuProps}
										actions={selectionActions}
										context={contextMenuActionContext}
										trackingId="email-list-item-context-menu"
										title={computeContextMenuTitle(
											contextMenuActionContext.effectiveSelection,
											(x) => x.subject || localize("_NoSubject")
										)}
									/>
								)}

								<DragTooltip
									ref={dragTooltipRef}
									trackingId="email-list-drag-tooltip"
								>
									{numberOfDraggedEmails === 1
										? localize("_MoveOneItem")
										: (
											<FormattedText
												text={localize("_MoveNItems")}
												thingsToReplace={[
													{ what: "{0}", filler: numberOfDraggedEmails }
												]}
											/>
										)
									}
								</DragTooltip>
							</>
						)
					}
				</div>
				{isListBusy && (
					<BusyIndicator />
				)}
				{uploadAttachmentInProgressDialog}
			</div>
		);
	}
);
ListView.displayName = "ListView";

function convertToEmailActionModel(item: EmailListItemWithMailbox): EmailActionModel {
	return {
		emailId: item.emailId,
		subject: item.subject,
		isDraft: item.isDraft,
		isFlagged: item.isFlagged,
		isRead: item.isRead,
		smtpAddress: item.smtpAddress
	};
}

function getUniqueKey(item: EmailListItemModel): string {
	return item.emailId
		.id;
}

const PAGE_SIZE = 50;

export type ItemsState = {
	items: readonly EmailListItemWithMailbox[];
	hasMore: boolean;
};

export type EmailListItemWithMailbox = EmailListItemModel & {
	smtpAddress: string | null;
};

type LoadNextItems = (itemState: ItemsState, requestParams: ExchangeEmailScreenSelectionModel) => Promise<void>;

function useLoadNextItems(
	itemLoadingAbortController: RefObject<AbortController | undefined>,
	openedEmailId: ExchangeEmailId | null,
	setIsBusy: (value: boolean) => void,
	setItemsState: Dispatch<SetStateAction<ItemsState>>,
	onWatermarkChange: (watermark: string) => void,
	onFolderCountersRetrieved: (counters: FolderCounters) => void,
	virtualListRef: ReadonlyRefObject<ScrollableVirtualListHandle<EmailListItemWithMailbox> | null>
): LoadNextItems {

	const stableOnWaterMarkChange: typeof onWatermarkChange = useStableImperativeCallback(onWatermarkChange);
	const openedEmailIdRef: ReadonlyRefObject<ExchangeEmailId | null> = useSynchronizedRef(openedEmailId);
	const stableOnFolderCountersRetrieved: typeof onFolderCountersRetrieved = useStableImperativeCallback(onFolderCountersRetrieved);

	return useCallback(
		async (
			itemState: ItemsState,
			requestParams: ExchangeEmailScreenSelectionModel
		): Promise<void> => {
			itemLoadingAbortController.current
				?.abort();

			const cancellation = new AbortController();
			itemLoadingAbortController.current = cancellation;

			setIsBusy(true);

			try {
				const { items } = itemState;

				const skip: number = items.length;

				const isFirstPage: boolean = skip === 0;

				const hasAnyFilter: boolean = isNotNilOrWhiteSpace(requestParams.searchQuery)
					|| isNotNil(requestParams.filterSelector);

				const includeFolderCounters: boolean = isFirstPage && !hasAnyFilter;

				let response: ListFromExchangePossibleWithFolderCountersResponse;
				if (includeFolderCounters) {
					response = await listFromExchangeWithFolderCounters(
						PAGE_SIZE,
						skip,
						requestParams,
						cancellation.signal
					);

					stableOnFolderCountersRetrieved({
						folderId: {
							id: requestParams.folder.folderId
						},
						total: response.folderTotalCount!,
						unread: response.folderUnreadCount!
					});
				} else {
					response = await listFromExchange(
						PAGE_SIZE,
						skip,
						requestParams,
						cancellation.signal
					);
				}

				const { folder } = requestParams;
				const smtpAddress: string | null = folder
					.smtpAddress
					.isSet
						? folder
							.smtpAddress
							.value
						: null;

				const newItems: readonly EmailListItemWithMailbox[] = response
					.data
					.map(x => ({ ...x, smtpAddress }));

				setItemsState(
					(x): ItemsState => ({
						items: [...x.items, ...newItems],
						hasMore: response.hasMore
					})
				);

				if (items.length === 0) {
					if (response.watermark) {
						stableOnWaterMarkChange(response.watermark);
					}

					const { current: openedEmailId } = openedEmailIdRef;
					if (openedEmailId) {
						const itemToAutoSelect: EmailListItemWithMailbox | undefined = newItems
							.find(x => x.emailId.id === openedEmailId.id);

						if (itemToAutoSelect) {
							virtualListRef.current
								?.scrollIntoView(itemToAutoSelect);
						}
					}
				}

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setItemsState(x => ({ ...x, hasMore: false }));
				setIsBusy(false);
			}
		},
		[
			itemLoadingAbortController,
			stableOnFolderCountersRetrieved,
			stableOnWaterMarkChange,
			openedEmailIdRef,
			setIsBusy,
			setItemsState,
			virtualListRef
		]
	);
}

function processUpdate(
	emailChanges: readonly EmailListItemModel[],
	setItemsState: Dispatch<SetStateAction<ItemsState>>,
	filter: EmailFilterSelector | null,
	requestParams: ExchangeEmailScreenSelectionModel
): void {
	const { folder } = requestParams;
	const smtpAddress: string | null = folder
		.smtpAddress
		.isSet
			? folder
				.smtpAddress
				.value
			: null;

	const itemsChanges: readonly EmailListItemWithMailbox[] = emailChanges
		.map(x => ({ ...x, smtpAddress }));

	setItemsState(
		(x): ItemsState => {
			const items: EmailListItemWithMailbox[] = [...x.items];

			for (const change of itemsChanges) {
				const indexOfExistingItem: number = items.findIndex((x) => x.emailId.id === change.emailId.id);

				if (indexOfExistingItem !== -1) {
					items.splice(
						indexOfExistingItem,
						1,
						change
					);

					continue;
				}

				if (filter === EmailFilterSelector.Flagged && !change.isFlagged) {
					continue;
				}

				if (filter === EmailFilterSelector.Unread && change.isRead) {
					continue;
				}

				if (filter === EmailFilterSelector.HasAttachment && !change.hasAttachments) {
					continue;
				}

				const indexInPage: number = EmailOrdering.findIndexToInsertInOrder(
					requestParams.sortOrder,
					requestParams.sortDirection,
					items,
					change
				);

				const isNewestEmailsSort: boolean = requestParams.sortOrder === EmailSortOrder.Date
					&& requestParams.sortDirection === EmailSortDirection.Descending;

				if (
					(indexInPage > 0 && indexInPage < items.length)
						|| (isNewestEmailsSort && indexInPage === 0)
				) {
					items.splice(
						indexInPage,
						0,
						change
					);
				}
			}

			return {
				...x,
				items
			};
		}
	);
}

type EmailIdWithIsDraftStatus = {
	emailId: ExchangeEmailId;
	isDraft: boolean;
};

function useSingleSelectedItem(selectedItems: readonly EmailActionModel[]): EmailIdWithIsDraftStatus | undefined {
	const singleSelectedItem = useMemo(
		(): EmailIdWithIsDraftStatus | undefined => {
			if (selectedItems.length !== 1) {
				return undefined;
			}

			const {
				emailId,
				isDraft
			} = selectedItems[0];

			return {
				emailId,
				isDraft
			};
		},
		[selectedItems]
	);

	return useValueEqualMemo(
		() => singleSelectedItem,
		[singleSelectedItem]
	);
}